import { useState, type FocusEvent } from 'react';
import {
  Box,
  Card,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Stack,
  Slider,
  Typography,
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  North as MoveUpIcon,
  South as MoveDownIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getAdminPairs, 
  createPair, 
  updatePair, 
  deletePair, 
  duplicatePair,
  getExchanges,
} from '@/services/api';
import { SectionHeader } from '@/components/common/Page';
import ExchangeLogo from '@/components/common/ExchangeLogo';
import { useAdminI18n } from '@/contexts/AdminI18nContext';
import type { ArbitragePair, CreatePairRequest, Exchange } from '@/types';

const DEFAULT_RIBBON_COLOR = '#54E38D';
const DEFAULT_SAFETY_PERCENT = 70;
const DEFAULT_NEGATIVE_SPREAD_PERCENT = -0.2;
type PairUpdateData = Partial<CreatePairRequest> & { display_order?: number };
type AdminPairsResponse = { pairs: ArbitragePair[] };
type ViewTransitionDocument = Document & {
  startViewTransition?: (updateCallback: () => void) => { finished: Promise<void> };
};

const clampPercent = (value: number) => Math.max(0, Math.min(100, Math.round(value)));

const colorStops = [
  { at: 0, color: '#FF6B7A' },
  { at: 45, color: '#FFB74D' },
  { at: 68, color: '#FFD166' },
  { at: 100, color: '#54E38D' },
];

const hexToRgb = (hex: string) => ({
  r: parseInt(hex.slice(1, 3), 16),
  g: parseInt(hex.slice(3, 5), 16),
  b: parseInt(hex.slice(5, 7), 16),
});

const rgbToHex = ({ r, g, b }: { r: number; g: number; b: number }) =>
  `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`.toUpperCase();

const safetyToRibbonColor = (safetyPercent: number) => {
  const value = clampPercent(safetyPercent);
  const upperIndex = colorStops.findIndex((stop) => value <= stop.at);
  const upper = colorStops[Math.max(upperIndex, 1)];
  const lower = colorStops[Math.max(upperIndex - 1, 0)];
  const range = upper.at - lower.at || 1;
  const amount = (value - lower.at) / range;
  const from = hexToRgb(lower.color);
  const to = hexToRgb(upper.color);

  return rgbToHex({
    r: Math.round(from.r + (to.r - from.r) * amount),
    g: Math.round(from.g + (to.g - from.g) * amount),
    b: Math.round(from.b + (to.b - from.b) * amount),
  });
};

const selectNumberInput = (event: FocusEvent<HTMLInputElement>) => {
  event.target.select();
};

const numberInputProps = {
  inputMode: 'decimal' as const,
};

const runPairListTransition = (update: () => void) => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const viewTransitionDocument = document as ViewTransitionDocument;

  if (!prefersReducedMotion && viewTransitionDocument.startViewTransition) {
    viewTransitionDocument.startViewTransition(update);
    return;
  }

  update();
};

export default function AdminPairsManager() {
  const queryClient = useQueryClient();
  const { t } = useAdminI18n();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPair, setEditingPair] = useState<ArbitragePair | null>(null);
  const [formData, setFormData] = useState<CreatePairRequest>({
    pair_type: 'single',
    coin_from: '',
    coin_to: '',
    spread_percent: 0,
    base_spread_percent: 0,
    negative_spread_percent: DEFAULT_NEGATIVE_SPREAD_PERCENT,
    exchange_buy: '',
    exchange_sell: '',
    is_active: true,
    access_level: 'basic',
    notes: '',
    ribbon_color: DEFAULT_RIBBON_COLOR,
    safety_percent: DEFAULT_SAFETY_PERCENT,
  });

  const { data: pairsData, isLoading } = useQuery({
    queryKey: ['admin-pairs'],
    queryFn: getAdminPairs,
  });

  const { data: exchangesData } = useQuery({
    queryKey: ['exchanges'],
    queryFn: getExchanges,
  });

  const invalidatePairQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-pairs'] });
    queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
    queryClient.invalidateQueries({ queryKey: ['pairs'] });
  };

  const createMutation = useMutation({
    mutationFn: createPair,
    onSuccess: () => {
      invalidatePairQueries();
      handleCloseDialog();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: PairUpdateData }) => updatePair(id, data),
    onSuccess: () => {
      invalidatePairQueries();
      handleCloseDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deletePair,
    onSuccess: () => {
      invalidatePairQueries();
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: duplicatePair,
    onSuccess: () => {
      invalidatePairQueries();
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (updates: Array<{ id: number; display_order: number }>) => {
      await Promise.all(
        updates.map((pair) => updatePair(pair.id, { display_order: pair.display_order })),
      );
    },
    onSuccess: () => {
      invalidatePairQueries();
    },
  });

  const handleOpenCreate = () => {
    setEditingPair(null);
    setFormData({
      pair_type: 'single',
      coin_from: '',
      coin_to: '',
      spread_percent: 0,
      base_spread_percent: 0,
      negative_spread_percent: DEFAULT_NEGATIVE_SPREAD_PERCENT,
      exchange_buy: '',
      exchange_sell: '',
      is_active: true,
      access_level: 'basic',
      notes: '',
      ribbon_color: DEFAULT_RIBBON_COLOR,
      safety_percent: DEFAULT_SAFETY_PERCENT,
    });
    setOpenDialog(true);
  };

  const handleOpenEdit = (pair: ArbitragePair) => {
    setEditingPair(pair);
    setFormData({
      pair_type: pair.pair_type,
      coin_from: pair.coin_from,
      coin_to: pair.coin_to || '',
      spread_percent: pair.base_spread_percent ?? pair.spread_percent ?? 0,
      base_spread_percent: pair.base_spread_percent ?? pair.spread_percent ?? 0,
      negative_spread_percent: pair.negative_spread_percent ?? DEFAULT_NEGATIVE_SPREAD_PERCENT,
      exchange_buy: pair.exchange_buy,
      exchange_sell: pair.exchange_sell,
      is_active: pair.is_active,
      access_level: pair.access_level,
      notes: pair.notes || '',
      ribbon_color: pair.ribbon_color || safetyToRibbonColor(pair.safety_percent ?? DEFAULT_SAFETY_PERCENT),
      safety_percent: pair.safety_percent ?? DEFAULT_SAFETY_PERCENT,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPair(null);
  };

  const handleSubmit = () => {
    const submitData = {
      ...formData,
      spread_percent: formData.base_spread_percent ?? formData.spread_percent,
    };

    if (editingPair) {
      updateMutation.mutate({ id: editingPair.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleSafetyChange = (value: number) => {
    const safetyPercent = clampPercent(value);
    setFormData({
      ...formData,
      safety_percent: safetyPercent,
      ribbon_color: safetyToRibbonColor(safetyPercent),
    });
  };

  const pairs = pairsData?.pairs ?? [];
  const activePairCount = pairs.filter((pair) => Boolean(pair.is_active)).length;
  const inactivePairCount = pairs.length - activePairCount;

  const handleMovePair = (fromIndex: number, direction: -1 | 1) => {
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= pairs.length) return;

    const reorderedPairs = [...pairs];
    const [movedPair] = reorderedPairs.splice(fromIndex, 1);
    reorderedPairs.splice(toIndex, 0, movedPair);
    const orderedPairs = reorderedPairs.map((pair, index) => ({
      ...pair,
      display_order: index + 1,
    }));

    runPairListTransition(() => {
      queryClient.setQueryData<AdminPairsResponse>(['admin-pairs'], (current) =>
        current ? { ...current, pairs: orderedPairs } : current,
      );
    });

    reorderMutation.mutate(
      orderedPairs.map((pair) => ({
        id: pair.id,
        display_order: pair.display_order,
      })),
    );
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      <SectionHeader
        title={t('admin.pairs.title')}
        detail={t('admin.pairs.detail', {
          configured: pairs.length,
          active: activePairCount,
          inactive: inactivePairCount,
        })}
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            {t('admin.pairs.addPair')}
          </Button>
        }
      />

      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.common.status')}</TableCell>
              <TableCell>{t('admin.pairs.position')}</TableCell>
              <TableCell>{t('admin.pairs.coins')}</TableCell>
              <TableCell>{t('admin.pairs.spread')}</TableCell>
              <TableCell>{t('admin.pairs.safety')}</TableCell>
              <TableCell>{t('admin.common.exchanges')}</TableCell>
              <TableCell>{t('admin.common.access')}</TableCell>
              <TableCell>{t('admin.pairs.note')}</TableCell>
              <TableCell>{t('admin.common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pairs.map((pair: ArbitragePair, index) => (
              <TableRow
                key={pair.id}
                sx={{
                  viewTransitionName: `admin-pair-${pair.id}`,
                  '@media (prefers-reduced-motion: no-preference)': {
                    transition:
                      'background-color 180ms ease, box-shadow 180ms ease, opacity 180ms ease',
                  },
                }}
              >
                <TableCell>
                  <Box
                    title={pair.is_active ? t('admin.common.active') : t('admin.common.inactive')}
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: pair.is_active ? 'success.main' : 'error.main',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 26, fontWeight: 800 }}>
                      #{index + 1}
                    </Typography>
                    <Stack
                      direction="row"
                      aria-label={t('admin.pairs.moveGroup', { coin: pair.coin_from })}
                      sx={{
                        overflow: 'hidden',
                        border: '1px solid rgba(226, 255, 238, 0.12)',
                        borderRadius: 999,
                        bgcolor: 'rgba(244, 255, 249, 0.035)',
                      }}
                    >
                      <IconButton
                        size="small"
                        aria-label={t('admin.pairs.moveUp', { coin: pair.coin_from })}
                        onClick={() => handleMovePair(index, -1)}
                        disabled={index === 0 || reorderMutation.isPending}
                        sx={{ width: 30, height: 30, borderRadius: 0 }}
                      >
                        <MoveUpIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                      <Box sx={{ width: 1, bgcolor: 'rgba(226, 255, 238, 0.10)' }} />
                      <IconButton
                        size="small"
                        aria-label={t('admin.pairs.moveDown', { coin: pair.coin_from })}
                        onClick={() => handleMovePair(index, 1)}
                        disabled={index === pairs.length - 1 || reorderMutation.isPending}
                        sx={{ width: 30, height: 30, borderRadius: 0 }}
                      >
                        <MoveDownIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Stack>
                  </Stack>
                </TableCell>
                <TableCell>
                  {pair.pair_type === 'single' 
                    ? pair.coin_from 
                    : `${pair.coin_from} -> ${pair.coin_to}`
                  }
                </TableCell>
                <TableCell>
                  <Stack spacing={0.25}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {(pair.base_spread_percent ?? pair.spread_percent) ?? 0}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('admin.pairs.negativeSpreadShort')}: {pair.negative_spread_percent ?? DEFAULT_NEGATIVE_SPREAD_PERCENT}%
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack spacing={0.75} sx={{ minWidth: 96 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: pair.ribbon_color || safetyToRibbonColor(pair.safety_percent ?? DEFAULT_SAFETY_PERCENT),
                          boxShadow: `0 0 0 3px ${
                            pair.ribbon_color || safetyToRibbonColor(pair.safety_percent ?? DEFAULT_SAFETY_PERCENT)
                          }22`,
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {pair.safety_percent ?? DEFAULT_SAFETY_PERCENT}%
                      </Typography>
                    </Stack>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack spacing={0.75}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <ExchangeLogo
                        src={pair.exchange_buy_logo_url}
                        name={pair.exchange_buy_name}
                        id={pair.exchange_buy}
                        size={22}
                      />
                      <Typography variant="body2">
                        {pair.exchange_buy_name || pair.exchange_buy}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <ExchangeLogo
                        src={pair.exchange_sell_logo_url}
                        name={pair.exchange_sell_name}
                        id={pair.exchange_sell}
                        size={22}
                      />
                      <Typography variant="body2">
                        {pair.exchange_sell_name || pair.exchange_sell}
                      </Typography>
                    </Stack>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip 
                    size="small" 
                    label={
                      pair.access_level === 'private'
                        ? t('admin.common.private')
                        : t('admin.common.basic')
                    }
                    color={pair.access_level === 'private' ? 'primary' : 'default'} 
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 260 }}>
                  <Typography
                    variant="body2"
                    color={pair.notes ? 'text.secondary' : 'text.disabled'}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {pair.notes || t('admin.common.noNote')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpenEdit(pair)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => duplicateMutation.mutate(pair.id)}>
                    <DuplicateIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => deleteMutation.mutate(pair.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPair ? t('admin.pairs.editPair') : t('admin.pairs.createPair')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('admin.pairs.pairType')}</InputLabel>
              <Select
                value={formData.pair_type}
                label={t('admin.pairs.pairType')}
                onChange={(e) => setFormData({ ...formData, pair_type: e.target.value as 'single' | 'double' })}
              >
                <MenuItem value="single">{t('admin.pairs.singleCoin')}</MenuItem>
                <MenuItem value="double">{t('admin.pairs.doubleCoin')}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label={t('admin.pairs.coinFrom')}
              value={formData.coin_from}
              onChange={(e) => setFormData({ ...formData, coin_from: e.target.value.toUpperCase() })}
              placeholder="BTC, ETH, USDT..."
            />

            {formData.pair_type === 'double' && (
              <TextField
                label={t('admin.pairs.coinTo')}
                value={formData.coin_to}
                onChange={(e) => setFormData({ ...formData, coin_to: e.target.value.toUpperCase() })}
                placeholder="RUB, USDT..."
              />
            )}

            <TextField
              label={t('admin.pairs.baseSpread')}
              type="number"
              inputProps={numberInputProps}
              value={formData.base_spread_percent ?? formData.spread_percent}
              onFocus={selectNumberInput}
              onChange={(e) => {
                const value = parseFloat(e.target.value) || 0;
                setFormData({ ...formData, spread_percent: value, base_spread_percent: value });
              }}
            />

            <TextField
              label={t('admin.pairs.negativeSpread')}
              type="number"
              inputProps={numberInputProps}
              value={formData.negative_spread_percent ?? DEFAULT_NEGATIVE_SPREAD_PERCENT}
              onFocus={selectNumberInput}
              onChange={(e) => setFormData({ ...formData, negative_spread_percent: parseFloat(e.target.value) || 0 })}
            />

            <FormControl fullWidth>
              <InputLabel>{t('admin.pairs.exchangeBuy')}</InputLabel>
              <Select
                value={formData.exchange_buy}
                label={t('admin.pairs.exchangeBuy')}
                onChange={(e) => setFormData({ ...formData, exchange_buy: e.target.value })}
              >
                {exchangesData?.exchanges.map((ex: Exchange) => (
                  <MenuItem key={ex.id} value={ex.id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ExchangeLogo src={ex.logo_url} name={ex.name} id={ex.id} size={24} />
                      <Typography>{ex.name}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('admin.pairs.exchangeSell')}</InputLabel>
              <Select
                value={formData.exchange_sell}
                label={t('admin.pairs.exchangeSell')}
                onChange={(e) => setFormData({ ...formData, exchange_sell: e.target.value })}
              >
                {exchangesData?.exchanges.map((ex: Exchange) => (
                  <MenuItem key={ex.id} value={ex.id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ExchangeLogo src={ex.logo_url} name={ex.name} id={ex.id} size={24} />
                      <Typography>{ex.name}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('admin.pairs.accessLevel')}</InputLabel>
              <Select
                value={formData.access_level}
                label={t('admin.pairs.accessLevel')}
                onChange={(e) => setFormData({ ...formData, access_level: e.target.value as 'basic' | 'private' })}
              >
                <MenuItem value="basic">{t('admin.pairs.basicAccess')}</MenuItem>
                <MenuItem value="private">{t('admin.pairs.privateAccess')}</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label={t('admin.common.active')}
            />

            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid rgba(226, 255, 238, 0.12)',
                bgcolor: 'rgba(244, 255, 249, 0.035)',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="subtitle2">{t('admin.pairs.safetySignal')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('admin.pairs.safetyValue', {
                      percent: formData.safety_percent ?? DEFAULT_SAFETY_PERCENT,
                    })}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: formData.ribbon_color || DEFAULT_RIBBON_COLOR,
                    border: '1px solid rgba(244, 255, 249, 0.22)',
                    boxShadow: `0 0 0 5px ${(formData.ribbon_color || DEFAULT_RIBBON_COLOR)}22`,
                    flexShrink: 0,
                  }}
                />
              </Stack>
              <Slider
                value={100 - (formData.safety_percent ?? DEFAULT_SAFETY_PERCENT)}
                min={0}
                max={100}
                step={1}
                onChange={(_, value) => handleSafetyChange(100 - (value as number))}
                aria-label={t('admin.pairs.safetyColor')}
                sx={{
                  mt: 2,
                  color: formData.ribbon_color || DEFAULT_RIBBON_COLOR,
                  '& .MuiSlider-rail': {
                    height: 10,
                    opacity: 1,
                    background: 'linear-gradient(90deg, #54E38D 0%, #FFD166 52%, #FFB74D 72%, #FF6B7A 100%)',
                  },
                  '& .MuiSlider-track': {
                    height: 10,
                    border: 0,
                    background: 'transparent',
                  },
                  '& .MuiSlider-thumb': {
                    width: 24,
                    height: 24,
                    bgcolor: formData.ribbon_color || DEFAULT_RIBBON_COLOR,
                    border: '3px solid #06120D',
                    boxShadow: `0 0 0 4px ${(formData.ribbon_color || DEFAULT_RIBBON_COLOR)}55`,
                  },
                }}
              />
              <Stack direction="row" justifyContent="space-between" sx={{ mt: -0.5 }}>
                <Typography variant="caption" color="success.main" sx={{ fontWeight: 800 }}>
                  {t('admin.pairs.safe')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  {t('admin.pairs.watch')}
                </Typography>
                <Typography variant="caption" color="error.main" sx={{ fontWeight: 800 }}>
                  {t('admin.pairs.risky')}
                </Typography>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 1.5 }}>
                <TextField
                  label={t('admin.pairs.safetyPercent')}
                  type="number"
                  inputProps={{ ...numberInputProps, min: 0, max: 100 }}
                  value={formData.safety_percent ?? DEFAULT_SAFETY_PERCENT}
                  onFocus={selectNumberInput}
                  onChange={(e) => handleSafetyChange(parseInt(e.target.value, 10) || 0)}
                  fullWidth
                />
                <TextField
                  label={t('admin.pairs.ribbonColor')}
                  value={formData.ribbon_color || DEFAULT_RIBBON_COLOR}
                  onChange={(e) => setFormData({ ...formData, ribbon_color: e.target.value })}
                  fullWidth
                />
              </Stack>
            </Box>

            <TextField
              label={t('admin.pairs.pairNote')}
              helperText={t('admin.pairs.pairNoteHelp')}
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('admin.common.cancel')}</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {editingPair ? t('admin.common.update') : t('admin.common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
